<template>
  <div>
    <popup-hint hint="estimates">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Estimates">
          <template v-slot:left v-if="pendingCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{pendingCount}} pending estimate{{pendingCount===1?'':'s'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.search='';filters.status='Pending';filters.salesRepId=null;filters.customerId=null;customerName=null})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <v-card width="400">
                <sales-preferences @update="usePicking=$event.usePicking;pickBeforeShip=$event.pickBeforeShip;lineItemPicking=$event.lineItemPicking"/>
              </v-card>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
    </popup-hint>

    <purtrak-table name="estimates" objectType="estimates" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-expand
                   listEndPoint="estimates" show-select v-model="selected"
                   fields="id,date,expires,number,salesRep{name},qbo,customer{id,name},privateNote,status,total,currency,exchangeRate,sent,email,discount{percent,amount,value}"
                   :totals.sync="totals" totalFields="totalValue"
                   :listFilters="listFilters"
                   :item-class="estimnate => (estimnate.expires < today ? 'grey--text ' : '')"
                   @dblclick="estimate => $router.push({name:'SalesEstimatesEdit',params:{estimateId:estimate.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   title="Estimates"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                           filters.search?searchDisplay:'',
                                           filters.salesRepId!=null?'Rep: ' + salesRepName:'']}"
                   download-endpoint="estimatesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.status='';filters.from=null;filters.to=null;filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'customerName'},{text:'Number',value:'number'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item>
            <v-select label="Status" outlined dense v-model="filters.status"
                      :items="[{text:'All',value:''},{divider:true},'Pending','Accepted','Closed','Rejected']"/>
          </v-list-item>
          <v-list-item v-if="salesReps && salesReps.length > 2 && !restrictedToSalesRep">
            <v-autocomplete label="Sales representative" dense outlined :items="salesReps" item-value="id" item-text="name" v-model="filters.salesRepId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search='';filters.customerId=null">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.status!=''" close small class="ml-1" @click:close="filters.status=''">{{filters.status}}</v-chip>
          <v-chip v-show="filters.salesRepId!=null" close small class="ml-1" @click:close="filters.salesRepId=null">{{salesRepName}}</v-chip>
          <v-chip v-show="filters.customerName" close small class="ml-1" @click:close="filters.customerId=null;filters.customerName=''">{{filters.customerName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'SalesEstimatesEditNew'}"><v-list-item-title>Create estimate</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="print()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:item.date="{item,printing}">
        {{item.date | formatDate}}
        <div v-if="!printing && item.expires < today" class="text-caption">Expired</div>
      </template>
      <template v-slot:item.expires="{item,printing}">
        <template v-if="printing">
          {{item.expires | formatDate}}
        </template>
        <div v-else :class="{'warning--text':item.expires<=today}">{{item.expires | formatDate}}</div>
      </template>
      <template v-slot:item.customerName="{item,printing}">
        {{item.customer.name}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.total="{item,value,printing}">
        {{item.currency===homeCurrency?'':item.currency}} {{value | currency}}
        <div v-if="!printing && item.currency!==homeCurrency" class="text-caption">{{value*item.exchangeRate | currency}}</div>
      </template>
      <template v-slot:item.status="{value,item,printing}">
        <template v-if="printing || readOnly">
          {{value}}
        </template>
        <v-hover v-else v-slot="{hover}">
          <a @click.stop="updateStatus(item)"
              :class="{'text-decoration-underline':hover,'error--text':value=='Rejected','success--text':value==='Accepted'}">
            {{value}}
          </a>
        </v-hover>
        <v-btn v-if="value==='Accepted'" rounded color="success" x-small :to="{name:'SalesSalesOrdersEditNew',params:{estimateId:item.id}}">Create SO</v-btn>
      </template>
      <template v-slot:item.qbo="{item,value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <template v-else-if="value">
          <div><v-icon>mdi-check</v-icon></div>
          <template v-if="connectedToAccounting && item.qbo && item.email">
            <v-btn v-if="!item.sent" rounded color="success" x-small @click="send(item)">Send</v-btn>
            <div v-else class="success--text">Sent</div>
          </template>
        </template>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'SalesEstimatesEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="200" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Estimate operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template>
              <template v-if="!readOnly">
                <template v-if="(item.status === 'Pending' || item.status === 'Accepted') && (!item.expires || item.expires >= today)">
                  <v-list-item :to="{name:'SalesSalesOrdersEditNew',params:{estimateId:item.id}}"><v-list-item-title>Create sales order</v-list-item-title></v-list-item>
                  <v-list-item :to="{name:'SalesShipmentsEditNew',params:{estimateId:item.id}}"><v-list-item-title>Create shipment</v-list-item-title></v-list-item>
                </template>
                <v-list-item @click="updateStatus(item)"><v-list-item-title>Update status</v-list-item-title></v-list-item>
                <v-divider/>
              </template>
              <v-list-item @click="print(item)"><v-list-item-title>Print</v-list-item-title></v-list-item>
              <template v-if="connectedToAccounting && item.qbo">
                <v-list-item @click="printQBO(item)"><v-list-item-title>View estimate from QBO</v-list-item-title></v-list-item>
                <v-list-item @click="send(item)"><v-list-item-title>Send estimate from QBO</v-list-item-title></v-list-item>
              </template>
              <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
              <v-divider/>
              <v-list-item :to="{name:'SalesEstimatesEdit',params:{estimateId:item.id}}"><v-list-item-title>Edit estimate</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'SalesEstimatesEditNew',params:{copyEstimateId:item.id}}"><v-list-item-title>Copy estimate</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item @click="estimate=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete estimate</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
            <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:70%">
              <thead>
                <tr>
                  <th :class="expansionPanelClass" width="40%">Description</th>
                  <th class="text-right" :class="expansionPanelClass" width="20%">Quantity</th>
                  <th class="text-right" :class="expansionPanelClass" width="20%">Unit price</th>
                  <th class="text-right" :class="expansionPanelClass" width="20%">Amount</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="line of item.lines">
                  <tr :key="line.key">
                    <td class="text-caption">{{line.description}}</td>
                    <td class="text-caption text-right">{{line.quantity | number(line.precision)}} {{line.units}}</td>
                    <td class="text-caption text-right">${{line.unitPrice | number(4)}}</td>
                    <td class="text-caption text-right">{{line.amount | currency}}</td>
                  </tr>
                </template>
                <tr v-if="item.discount.amount || (item.taxLines && item.taxLines.length)">
                  <td class="text-caption text-right" colspan="3">Subtotal</td>
                  <td class="text-caption text-right">{{item.subTotal | currency}}</td>
                </tr>
                <tr v-if="item.discount.amount">
                  <td class="text-caption text-right" colspan="3">Discount
                    <template v-if="item.discount.percent">{{item.discount.amount | number(2)}}%</template>
                    <template v-else>{{item.discount.amount | currency}}</template>
                  </td>
                  <td class="text-caption text-right">- {{item.discount.value | currency}}</td>
                </tr>
                <template v-for="tax of item.taxLines">
                  <tr :key="tax.id">
                    <td class="text-caption text-right" colspan="3">
                      {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                    </td>
                    <td class="text-caption text-right">{{tax.amount | currency}}</td>
                  </tr>
                </template>
                <tr>
                  <td class="text-caption text-right" colspan="3">Total</td>
                  <td class="text-caption text-right">{{item.total | currency}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='total'">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <component :is="dialog" v-bind="dialogProperties" :estimate="estimate" :estimates="selected" @done="dialog = null;$nextTick(() => getStatus())"/>
    <delete-object v-model="showDelete" name="the selected estimate" @delete="performDelete(estimate.id)"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import SalesPreferences from '@/views/sales/SalesPreferences'
import {format} from 'date-fns'

export default {
  name: 'SalesEstimates',
  mixins: [performDelete],
  components: {SalesPreferences},
  data() {
    return {
      filters: {
        status: '',
        from: null,
        to: null,
        customerId: null,
        search: '',
        searchField: 'customerName',
        salesRepId: null
      },
      totals: {totalValue:0},
      today: format(new Date(), 'yyyy-MM-dd'),
      pendingCount: 0,
      showStatus: false,
      dateDisplay: '',
      searchDisplay: '',
      estimate: {},
      salesReps: [{id:null,name:'All'},{divider:true}],
      showDelete: false,
			dialog: null,
      dialogProperties: {},
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'customerName', visible:true},
                       { value: 'total', visible:true},
                       { value: 'status', visible:true},
                       { value: 'expires', visible:true},
                       { value: 'salesRep.name', visible:false},
                       { value: 'qbo', visible:false},
                       { value: 'privateNote', visible:false}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'NUMBER', value: 'number' ,width:'10%',sortable:true},
                { text: 'CUSTOMER', value: 'customerName' ,width:'30%',sortable:true},
                { text: 'TOTAL', value: 'total' ,width:'10%',sortable:true,align:'right'},
                { text: 'STATUS', value: 'status' ,width:'10%',sortable:true,align:'center'},
                { text: 'EXPIRES', value: 'expires' ,width:'15%',sortable:true},
                { text: 'SALES REP', value: 'salesRep.name' ,width:'20%',sortable:true},
                { text: 'QBO', value: 'qbo' ,width:'10%',sortable:false, align:'center',omit:() => !this.connectedToAccounting},
                { text: 'NOTE', value: 'privateNote' ,width:'30%',sortable:false}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    readOnly() {
      return !this.company.permissions.includes('selling.write');
    },
    salesRepName() {
      return this.salesReps?.find(salesRep => salesRep.id === this.filters.salesRepId)?.name ?? '';
    },
    restrictedToSalesRep() {
      return this.salesReps.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted);
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.customerId !== null)
        filters.push({field:'customerId', comparator:'eq', value:this.filters.customerId});
      if (this.filters.salesRepId)
        filters.push({field:'salesRepId', comparator:'eq', value:this.filters.salesRepId});
      if (this.filters.status !== '')
        filters.push({field:'status', comparator:'eq', value:this.filters.status});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,restricted,userId', 100, [{field:'active', comparator:'eq', value:'true'}])

    await this.getStatus();
    if (!['SalesEstimatesEdit', 'SalesEstimatesEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.pendingCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    invoiceInfo({invoice}) {
      return `${invoice.type} ${invoice.number} ${this.$currency(invoice.total)}`;
    },
    async send(estimate) {
      if (!estimate.email) {
        this.$store.commit('setError', {type:'warning',message:`Cannot send this estimate since is doesn't have an email address`});
        return;
      }
      this.loading = true;
      const results = await this.$apiCall(`mutation SalesEstimates($id: ID!) {
                                              estimateSend(id:$id) {
                                                sent
                                              }
                                            }`,
                                          {id:estimate.id});
      this.loading = false;
      if (!results.error) {
        estimate.sent = results.data.estimateSend.sent;
        this.$store.commit('setError', {type:'success',message:`Estimate sent from QuickBooks Online`});
      }
    },
    async printQBO(estimate) {
      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimates($id: ID!) {
                                                estimatePrint(id:$id)
                                              }`,
                                          {id:estimate.id});
      this.loading = false;
      if (!results.error) {
        const win = window.open();
        if (!win) {
          this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
          return;
        }
        const document = win.document;
        const obj = document.createElement('object');
        obj.style.width="100%";
        obj.style.height="100%";
        obj.type = 'application/pdf';
        obj.data = 'data:application/pdf;base64,' + results.data.estimatePrint;
        document.body.appendChild(obj);
        win.document.close();
        win.focus();
      }
    },
    async expand(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesEstimates($id:ID!) {
                                                estimate(id:$id) {
                                                  subTotal
                                                  lines {
                                                    description,quantity,units,precision,unitPrice,amount,id
                                                  }
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        item.subTotal = results.data.estimate.subTotal;
        this.$set(item, 'lines', results.data.estimate.lines);
        this.$set(item, 'taxLines', results.data.estimate.taxLines);
        let key = 1;
        item.lines.forEach(line => line.key = key++);
        this.loading = false;
      }
    },
    async getStatus() {
      this.loading = true;
      const results = await this.$apiCall(`query SalesEstimates {
                                                estimatesPending
                                              }`);
      if (!results.error)
        this.pendingCount = results.data.estimatesPending;
      this.loading = false;
    },
    updatePrivateNote(estimate) {
      this.dialogProperties = {item:estimate, operation:'estimateUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    },
    updateStatus(estimate) {
      this.estimate = estimate;
      this.dialog = () => import(`@/views/sales/estimates/SalesEstimatesUpdateStatus`);
    },
    print(estimate) {
      this.estimate = estimate;
      this.dialog = () => import(`@/views/sales/estimates/SalesEstimatesPrint`);
    }
  }
}
</script>